import React, {useCallback, useContext, useEffect, useRef, useState} from "react";
import {Badge, Button, Col, Collapse, Container, Form, Modal, Row, Spinner, Table} from "react-bootstrap";
import {BackendContext} from "../../context/Backend";
import styles from "./usermanagement.module.css";
import {useTranslation} from "react-i18next";
import debounce from "lodash.debounce";

const PERMISSION_FIELDS = [
    "permRead",
    "permWrite",
    "permSign",
    "permFormFields"
];

const emptyDelegation = () => ({
    delegateUserId: "",
    envelopeId: null,
    permRead: 1,
    permWrite: 0,
    permSign: 0,
    permFormFields: 0,
    activeFrom: "",
    activeTo: ""
});

const formatDate = (ts) => {
    if (!ts) return "-";
    const d = new Date(ts);
    return d.toLocaleDateString();
};

const isActive = (delegation) => {
    const now = Date.now();
    const from = delegation.activeFrom ? new Date(delegation.activeFrom).getTime() : 0;
    const to = delegation.activeTo ? new Date(delegation.activeTo).getTime() : Infinity;
    return now >= from && now <= to;
};

const toDateInputValue = (ts) => {
    if (!ts) return "";
    return new Date(ts).toISOString().substring(0, 10);
};

const DelegationRow = ({delegation, labelUserKey, labelUserValue, t, onDelete, onUpdate}) => {
    const [expanded, setExpanded] = useState(false);
    const [perms, setPerms] = useState({});
    const [dates, setDates] = useState({activeFrom: "", activeTo: ""});
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        const p = {};
        PERMISSION_FIELDS.forEach(f => { p[f] = delegation[f] === 1; });
        setPerms(p);
        setDates({
            activeFrom: toDateInputValue(delegation.activeFrom),
            activeTo: toDateInputValue(delegation.activeTo)
        });
    }, [delegation]);

    const handlePermChange = (field) => {
        if (field === 'permRead') return;
        setPerms(prev => ({...prev, [field]: !prev[field]}));
    };

    const handleSave = () => {
        setSaving(true);
        const updated = {};
        PERMISSION_FIELDS.forEach(f => { updated[f] = perms[f] ? 1 : 0; });
        updated.activeFrom = dates.activeFrom ? `${dates.activeFrom}T00:00:00Z` : null;
        updated.activeTo = dates.activeTo ? `${dates.activeTo}T23:59:59Z` : null;
        onUpdate(delegation.delegationId, updated, () => setSaving(false));
    };

    const active = isActive(delegation);

    return (
        <>
            <tr onClick={() => setExpanded(!expanded)} className={styles.clickableRow}>
                <td>{labelUserValue}</td>
                <td>{formatDate(delegation.activeFrom)}</td>
                <td>{formatDate(delegation.activeTo)}</td>
                <td>
                    <Badge bg={active ? "success" : "secondary"}>
                        {active ? t('delegates.active') : t('delegates.inactive')}
                    </Badge>
                </td>
                <td>
                    {delegation.envelopeId
                        ? t('delegates.envelopeSpecific')
                        : t('delegates.userWide')}
                </td>
                <td>
                    <Button variant={"danger"} size={"sm"} className={"websobutton"} onClick={(e) => {
                        e.stopPropagation();
                        onDelete(delegation);
                    }}>
                        {t('delegates.delete')}
                    </Button>
                </td>
            </tr>
            <tr>
                <td colSpan={6} className={"p-0"}>
                    <Collapse in={expanded}>
                        <div className={"p-3"}>
                            <strong>{t('delegates.permissions')}</strong>
                            <Row className={"mt-2"}>
                                {PERMISSION_FIELDS.map(f => (
                                    <Col xs={6} md={4} key={f}>
                                        <Form.Check
                                            id={`perm-${delegation.delegationId}-${f}`}
                                            label={t('delegates.' + f)}
                                            checked={!!perms[f]}
                                            disabled={f === 'permRead'}
                                            onChange={() => handlePermChange(f)}
                                        />
                                    </Col>
                                ))}
                            </Row>
                            <Row className={"mt-3"}>
                                <Col xs={6} md={4}>
                                    <div className={"inputContainer"}>
                                        <Form.Label className={styles.label}>{t('delegates.validFrom')}</Form.Label>
                                        <Form.Control
                                            type="date"
                                            size="sm"
                                            value={dates.activeFrom}
                                            onChange={(e) => setDates(prev => ({...prev, activeFrom: e.target.value}))}
                                        />
                                    </div>
                                </Col>
                                <Col xs={6} md={4}>
                                    <div className={"inputContainer"}>
                                        <Form.Label className={styles.label}>{t('delegates.validTo')}</Form.Label>
                                        <Form.Control
                                            type="date"
                                            size="sm"
                                            value={dates.activeTo}
                                            onChange={(e) => setDates(prev => ({...prev, activeTo: e.target.value}))}
                                        />
                                    </div>
                                </Col>
                            </Row>
                            <Button className={"websobutton mt-2"} size={"sm"} onClick={handleSave} disabled={saving}>
                                {saving && <Spinner as="span" animation="border" size="sm" className={"me-1"}/>}
                                {t('delegates.save')}
                            </Button>
                        </div>
                    </Collapse>
                </td>
            </tr>
        </>
    );
};

const DelegationTable = ({delegations, labelUserKey, loginField, t, onDelete, onUpdate}) => {
    if (!delegations || delegations.length === 0) {
        return <p className={"text-muted mt-2"}>{t('delegates.noResults')}</p>;
    }

    return (
        <Table hover size={"sm"} className={"mt-2"}>
            <thead>
            <tr>
                <th>{t('delegates.' + labelUserKey)}</th>
                <th>{t('delegates.validFrom')}</th>
                <th>{t('delegates.validTo')}</th>
                <th>{t('delegates.status')}</th>
                <th></th>
                <th></th>
            </tr>
            </thead>
            <tbody>
            {delegations.map(d => (
                <DelegationRow
                    key={d.delegationId}
                    delegation={d}
                    labelUserKey={labelUserKey}
                    labelUserValue={d[loginField] || d.delegateUserId || d.principalUserId}
                    t={t}
                    onDelete={onDelete}
                    onUpdate={onUpdate}
                />
            ))}
            </tbody>
        </Table>
    );
};

export const DelegationsTab = ({userId}) => {
    const {t} = useTranslation();
    const backend = useContext(BackendContext);

    const [ownDelegations, setOwnDelegations] = useState([]);
    const [grantedDelegations, setGrantedDelegations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [newDelegation, setNewDelegation] = useState(emptyDelegation());
    const [creating, setCreating] = useState(false);
    const [userSearch, setUserSearch] = useState("");
    const [userResults, setUserResults] = useState([]);
    const [selectedUserName, setSelectedUserName] = useState("");
    const userSearchRef = useRef(null);

    const debouncedUserSearch = useCallback(
        debounce((value) => {
            if (value.length < 2) { setUserResults([]); return; }
            backend.api.searchUserManagementUsers(
                {search: value}, 0, 10,
                (data) => setUserResults(data.users || []),
                () => setUserResults([])
            );
        }, 300),
        []
    );

    const handleUserSearchChange = (e) => {
        const value = e.target.value;
        setUserSearch(value);
        setSelectedUserName("");
        setNewDelegation(prev => ({...prev, delegateUserId: ""}));
        debouncedUserSearch(value);
    };

    const handleSelectUser = (user) => {
        const displayName = [user.firstName, user.lastName].filter(Boolean).join(" ") || user.login;
        setSelectedUserName(displayName + " (" + user.login + ")");
        setUserSearch("");
        setUserResults([]);
        setNewDelegation(prev => ({...prev, delegateUserId: user.userId}));
    };

    const loadDelegations = () => {
        setLoading(true);
        let pending = 2;
        const done = () => { pending--; if (pending === 0) setLoading(false); };

        backend.api.getDelegationsForUser(userId, (data) => {
            setOwnDelegations(Array.isArray(data) ? data : []);
            done();
        }, () => { done(); });

        backend.api.getGrantedDelegationsForUser(userId, (data) => {
            setGrantedDelegations(Array.isArray(data) ? data : []);
            done();
        }, () => { done(); });
    };

    useEffect(() => {
        loadDelegations();
    }, [userId]);

    const handleDelete = (delegation) => {
        setDeleteTarget(delegation);
    };

    const confirmDelete = () => {
        if (!deleteTarget) return;
        backend.api.deleteDelegation(deleteTarget.delegationId, () => {
            setDeleteTarget(null);
            loadDelegations();
        }, () => {
            setDeleteTarget(null);
        });
    };

    const handleUpdate = (delegationId, permUpdates, onDone) => {
        backend.api.updateDelegation(delegationId, permUpdates, () => {
            loadDelegations();
            onDone();
        }, () => { onDone(); });
    };

    const handleNewChange = (field, value) => {
        setNewDelegation(prev => ({...prev, [field]: value}));
    };

    const handleNewPermChange = (field) => {
        if (field === 'permRead') return;
        setNewDelegation(prev => ({...prev, [field]: prev[field] === 1 ? 0 : 1}));
    };

    const handleCreate = () => {
        setCreating(true);
        const payload = {
            ...newDelegation,
            delegateUserId: newDelegation.delegateUserId,
            activeFrom: newDelegation.activeFrom ? `${newDelegation.activeFrom}T00:00:00Z` : null,
            activeTo: newDelegation.activeTo ? `${newDelegation.activeTo}T23:59:59Z` : null,
            envelopeId: newDelegation.envelopeId || null
        };
        backend.api.createDelegationForUser(userId, payload, true, () => {
            setNewDelegation(emptyDelegation());
            setSelectedUserName("");
            setUserSearch("");
            setCreating(false);
            loadDelegations();
        }, () => { setCreating(false); });
    };

    if (loading) {
        return (
            <Container fluid={true} className={"py-3 text-center"}>
                <Spinner animation="border"/>
            </Container>
        );
    }

    return (
        <Container fluid={true} className={"py-3"}>
            <h5>{t('delegates.grantedDelegations')}</h5>
            <DelegationTable
                delegations={grantedDelegations}
                labelUserKey={"principalUser"}
                loginField={"principalLogin"}
                t={t}
                onDelete={handleDelete}
                onUpdate={handleUpdate}
            />

            <hr/>

            <h5>{t('delegates.ownDelegations')}</h5>
            <DelegationTable
                delegations={ownDelegations}
                labelUserKey={"delegateUser"}
                loginField={"delegateLogin"}
                t={t}
                onDelete={handleDelete}
                onUpdate={handleUpdate}
            />

            <hr/>

            <h5>{t('delegates.create')}</h5>
            <Row className={"mb-2"}>
                <Col xs={12} md={4} className={styles.userSearchWrap}>
                    <div className={"inputContainer"}>
                        <Form.Label className={styles.label}>{t('delegates.delegateUser')}</Form.Label>
                        {selectedUserName ? (
                            <div className={"d-flex align-items-center"}>
                                <Form.Control type="text" value={selectedUserName} readOnly size="sm"/>
                                <Button variant={"outline-secondary"} size={"sm"} className={"websobutton ms-1"}
                                    onClick={() => { setSelectedUserName(""); setNewDelegation(prev => ({...prev, delegateUserId: ""})); }}>
                                    &times;
                                </Button>
                            </div>
                        ) : (
                            <Form.Control
                                type="text"
                                ref={userSearchRef}
                                value={userSearch}
                                onChange={handleUserSearchChange}
                                placeholder={t('delegates.searchUser')}
                                autoComplete="off"
                            />
                        )}
                    </div>
                    {userResults.length > 0 && (
                        <div className={styles.searchDropdownContainer}>
                            {userResults.map(u => (
                                <div key={u.userId} className={styles.searchDropdownItem}
                                    onMouseDown={() => handleSelectUser(u)}>
                                    {[u.firstName, u.lastName].filter(Boolean).join(" ")} <span className={"text-muted"}>({u.login})</span>
                                </div>
                            ))}
                        </div>
                    )}
                </Col>
                <Col xs={12} md={4}>
                    <div className={"inputContainer"}>
                        <Form.Label className={styles.label}>{t('delegates.validFrom')}</Form.Label>
                        <Form.Control
                            type="date"
                            value={newDelegation.activeFrom}
                            onChange={(e) => handleNewChange("activeFrom", e.target.value)}
                        />
                    </div>
                </Col>
                <Col xs={12} md={4}>
                    <div className={"inputContainer"}>
                        <Form.Label className={styles.label}>{t('delegates.validTo')}</Form.Label>
                        <Form.Control
                            type="date"
                            value={newDelegation.activeTo}
                            onChange={(e) => handleNewChange("activeTo", e.target.value)}
                        />
                    </div>
                </Col>
            </Row>
            <Row className={"mb-2"}>
                {PERMISSION_FIELDS.map(f => (
                    <Col xs={6} md={4} key={f}>
                        <Form.Check
                            id={`new-perm-${f}`}
                            label={t('delegates.' + f)}
                            checked={newDelegation[f] === 1}
                            disabled={f === 'permRead'}
                            onChange={() => handleNewPermChange(f)}
                        />
                    </Col>
                ))}
            </Row>
            <Button className={"websobutton"} size={"sm"} onClick={handleCreate} disabled={creating || !newDelegation.delegateUserId}>
                {creating && <Spinner as="span" animation="border" size="sm" className={"me-1"}/>}
                {t('delegates.create')}
            </Button>

            <Modal show={deleteTarget != null} backdrop="static" keyboard={false} onHide={() => setDeleteTarget(null)}>
                <Modal.Header closeButton>
                    <Modal.Title className={"text-danger"}>{t('delegates.delete')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>{t('delegates.confirmDelete')}</Modal.Body>
                <Modal.Footer>
                    <Button className={"websobutton"} onClick={() => setDeleteTarget(null)}>
                        {t('system.cancelButton')}
                    </Button>
                    <Button variant={"danger"} className={"websobutton"} onClick={confirmDelete}>
                        {t('delegates.delete')}
                    </Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
};
